import { createSelector } from "reselect"
import { get, map, first, isNil } from "lodash"
import moment from "moment"
import { getStartDate, getEndDate } from "selectors/dates"
import { getDatesAdjusted as getDatesAdjustedHelper } from "helpers/dates"
import { getPeriod } from "modules/portfolio-root/selectors/portfolios"
import { getLatestDate } from "modules/portfolio-root/selectors/dates"

const getPerformanceHistory = state => get(state, "global.data.axiomaPerformanceHistory.data.model.simulation.performance")

export const getTrends = createSelector(
  [getPerformanceHistory],
  (performanceHistory) => map(performanceHistory,
    (item) => ({
      date: moment(item.date).toDate(),
      total: get(item, "totalReturn.cumulativePercent"),
      alpha: get(item, "attribution.summary.specific.cumulativePercent"),
      factor: get(item, "attribution.summary.factors.cumulativePercent"),
      tradingPnl: get(item, "attribution.summary.trading.cumulativePercent"),
      residual: get(item, "attribution.summary.residual.cumulativePercent"),
    })),
)

export const getCumulativeTrend = createSelector(
  [getTrends],
  (trends) => trends.map(({ date, total }, index) => ({
    date, value: total, index,
  })),
)

export const getAlphaTrend = createSelector(
  [getTrends],
  (trends) => trends.map(({ date, alpha }, index) => ({
    date, value: alpha, index,
  })),
)

export const getTradingPnl = createSelector(
  [getTrends],
  (trends) => {
    let showTrading = false
    const tradingPnl = trends.map((trend, index) => {
      if (trend.tradingPnl) showTrading = true
      return { date: trend.date, value: trend.tradingPnl || 0, index }
    })

    return showTrading ? tradingPnl : null
  },
)

export const getFactorTrend = createSelector(
  [getTrends],
  (trends) => trends.map(({ date, factor }, index) => ({
    date, value: factor, index,
  })),
)

export const getResidualTrend = createSelector(
  [getTrends],
  (trends) => {
    let showResidual = false
    const residualTrend = trends.map((trend, index) => {
      if (!isNil(trend.residual)) showResidual = true
      return { date: trend.date, value: trend.residual ?? 0, index }
    })

    return showResidual ? residualTrend : null
  },
)

// We need the first because the performance will start the day after our portfolio starts
// so the selected period might not exactly match the result
export const getFirstDate = createSelector(
  [getStartDate, getTrends],
  (startDate, trends) => {
    return moment(get(first(trends), "date", startDate)).format("YYYY-MM-DD")
  },
)

export const getDatesAdjusted = createSelector(
  [getPeriod, getStartDate, getEndDate, getLatestDate],
  getDatesAdjustedHelper,
)
